import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Image, VideoComponent, VideoContainer } from "./Video.styles";

const Video = forwardRef(
  (
    {
      src,
      imageSrc,
      className,
      onClick,
      controls = false,
      disabled,
      autoplay,
      preload = "metadata",
      onMediaReady,
    },
    ref,
  ) => {
    const [loaded, setLoaded] = useState(false);
    const [posterLoaded, setPosterLoaded] = useState(false);
    const videoRef = useRef(null);
    const posterRef = useRef(null);
    const reportedReadyRef = useRef(false);

    const setVideoRef = useCallback(
      (node) => {
        videoRef.current = node;

        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );

    const handleVideoLoaded = () => {
      setLoaded(true);
    };

    /* Cache safety net: when imageSrc changes (or on first mount), the
       <img> may already be complete from cache before React attached
       the onLoad listener — Safari is especially eager about this. We
       reset posterLoaded then synchronously check img.complete so cached
       posters don't stay stuck at opacity:0. */
    useEffect(() => {
      setPosterLoaded(false);
      if (!imageSrc) return;
      const img = posterRef.current;
      if (img && img.complete && img.naturalWidth > 0) {
        setPosterLoaded(true);
      }
    }, [imageSrc]);

    /* Own the src lifecycle in one place. React removing a <source> child is
       not enough for Safari to free the decoder, so detach the src attribute
       and call load() when parents intentionally pass null. */
    useEffect(() => {
      const video = videoRef.current;
      if (!video) return;

      setLoaded(false);

      if (src) {
        if (video.getAttribute("src") !== src) {
          video.src = src;
          try {
            video.load();
          } catch (_) {
            /* noop */
          }
        }
        return;
      }

      try {
        video.pause();
      } catch (_) {
        /* noop */
      }

      if (video.hasAttribute("src")) {
        video.removeAttribute("src");
        try {
          video.load();
        } catch (_) {
          /* noop */
        }
      }
    }, [src]);

    /* Show the video only when it's loaded AND src is currently attached.
       When src detaches (parent freeing the decoder), opacity drops to 0
       and the always-mounted poster underneath stays in place — no
       mount/unmount flash. */
    const videoVisible = loaded && !!src;

    /* Notify the parent the first time something visual lands, so it can
       drop a structured skeleton overlay. We only fire once per mount —
       the parent is expected to reset its state when items get recycled. */
    useEffect(() => {
      if (reportedReadyRef.current) return;
      if (posterLoaded || videoVisible) {
        reportedReadyRef.current = true;
        if (typeof onMediaReady === "function") onMediaReady();
      }
    }, [posterLoaded, videoVisible, onMediaReady]);

    return (
      <VideoContainer
        className={className}
        onClick={onClick}
        $cursorPointer={!!onClick && !disabled}
      >
        {/* Poster is always mounted in the background. Hidden via opacity
            until it actually loads — without this gate, the browser flashes
            its default broken-image glyph for the brief window between the
            <img> mounting and the byte arrival. */}
        {imageSrc && (
          <Image
            ref={posterRef}
            src={imageSrc}
            alt=""
            loading="lazy"
            decoding="async"
            onLoad={() => setPosterLoaded(true)}
            style={{ opacity: posterLoaded ? 1 : 0 }}
          />
        )}
        <VideoComponent
          controls={controls}
          onLoadedData={handleVideoLoaded}
          muted
          loop
          playsInline
          ref={setVideoRef}
          autoPlay={!!autoplay}
          preload={src ? preload : "none"}
          poster={imageSrc}
          style={{ opacity: videoVisible ? 1 : 0 }}
        />
      </VideoContainer>
    );
  },
);

export default Video;
